import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { getArticleTranslations, translateHeadlines } from "@/lib/articleAi.functions";
import { TRANSLATION_CODE } from "@/i18n/config";
import { useLocale } from "@/i18n/useLocale";

export interface HeadlineText {
  title: string;
  excerpt: string;
}

interface Store {
  enabled: boolean;
  entries: Record<string, HeadlineText>;
  register: (id: string) => void;
}

const ArticleTranslationContext = createContext<Store>({
  enabled: false,
  entries: {},
  register: () => {},
});

const BATCH_DELAY = 150;
const TRANSLATE_BATCH = 12;

export const ArticleTranslationProvider = ({ children }: { children: ReactNode }) => {
  const locale = useLocale();
  const language = TRANSLATION_CODE[locale];
  const enabled = Boolean(language);

  const [entries, setEntries] = useState<Record<string, HeadlineText>>({});
  const requested = useRef<Set<string>>(new Set());
  const pending = useRef<Set<string>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(false);

  const flush = useCallback(async () => {
    if (!language || inFlight.current) return;
    const ids = Array.from(pending.current);
    pending.current.clear();
    if (!ids.length) return;

    inFlight.current = true;
    try {
      const cached = await getArticleTranslations({ data: { ids, language } });
      const found = new Map(cached.items.map((item) => [item.id, item]));
      if (found.size) {
        setEntries((prev) => {
          const next = { ...prev };
          for (const [id, item] of found) next[id] = { title: item.title, excerpt: item.excerpt };
          return next;
        });
      }

      // Anything not saved yet gets translated now, in a small batch, and cached
      // server-side so the next reader sees it instantly.
      const missing = ids.filter((id) => !found.has(id)).slice(0, TRANSLATE_BATCH);
      if (missing.length) {
        const fresh = await translateHeadlines({ data: { ids: missing, language } });
        if (fresh.items.length) {
          setEntries((prev) => {
            const next = { ...prev };
            for (const item of fresh.items) next[item.id] = { title: item.title, excerpt: item.excerpt };
            return next;
          });
        }
      }
    } catch (error) {
      console.error("headline translations unavailable", error);
    } finally {
      inFlight.current = false;
      if (pending.current.size) {
        timer.current = setTimeout(() => void flush(), BATCH_DELAY);
      }
    }
  }, [language]);

  const register = useCallback(
    (id: string) => {
      if (!enabled || !id || requested.current.has(id)) return;
      requested.current.add(id);
      pending.current.add(id);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), BATCH_DELAY);
    },
    [enabled, flush],
  );

  useEffect(() => {
    // A language switch is a full page load, but clear state defensively.
    requested.current.clear();
    pending.current.clear();
    setEntries({});
  }, [language]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const value = useMemo(() => ({ enabled, entries, register }), [enabled, entries, register]);

  return (
    <ArticleTranslationContext.Provider value={value}>{children}</ArticleTranslationContext.Provider>
  );
};

/** Translated headline/summary for one story, or null on the English edition. */
export const useArticleHeadline = (
  id: string | undefined,
  fallback: { title: string; excerpt?: string | null },
) => {
  const { enabled, entries, register } = useContext(ArticleTranslationContext);

  useEffect(() => {
    if (enabled && id) register(id);
  }, [enabled, id, register]);

  if (!enabled || !id) return { title: fallback.title, excerpt: fallback.excerpt ?? "" };
  const found = entries[id];
  return {
    title: found?.title || fallback.title,
    excerpt: found?.excerpt || fallback.excerpt || "",
  };
};
